<template>
  <div class="solar-plant-solar-panels">
    <div v-if="!solarPanels || solarPanels.length === 0" class="text-muted text-center py-3">
      <i class="fa fa-sun-o fa-2x mb-2 d-block"></i>
      {{ $t('no_solar_panels_added') }}
    </div>
    <div v-else>
      <b-row
        v-for="(panel, index) in solarPanels"
        :key="getSolarPanelKey(panel, index)"
        class="solar-panel-row mb-2"
      >
        <b-col lg="10" md="6" sm="12">
          <div class="d-flex align-items-center">
            <div class="solar-panel-info flex-grow-1">
              <router-link
                v-if="panel.item && panel.item.number"
                target="_blank"
                :to="{
                  name: 'ItemEditPage',
                  params: { itemNumber: panel.item.number },
                }"
                class="mr-2"
              >
                #{{ panel.item.number.toString().padStart(4, '0') }}
                <i class="fa fa-sm fa-external-link"></i>
              </router-link>
              <span v-if="getManufacturer(panel.item)">
                {{ getManufacturer(panel.item) }} - {{ getType(panel.item) }}
              </span>
              <span v-else class="text-muted">-</span>
              <span v-if="solarPanelPower(panel.item)" class="ml-2 text-muted">
                ({{ solarPanelPower(panel.item) }} Wp)
              </span>
            </div>
            <div class="quantity-controls d-flex align-items-center ml-3">
              <button
                class="btn btn-sm btn-danger"
                @click="decreaseCount(index)"
                :disabled="panel.count <= 1"
              >
                <i class="fa fa-minus"></i>
              </button>
              <input
                v-model.number="panel.count"
                type="number"
                min="1"
                class="form-control form-control-sm mx-1 quantity-input"
                @input="updateCount(index, $event.target.value)"
              />
              <button class="btn btn-sm btn-success" @click="increaseCount(index)">
                <i class="fa fa-plus"></i>
              </button>
            </div>
            <button
              class="btn btn-sm btn-danger ml-2"
              @click="removeSolarPanel(index)"
              :title="$t('remove')"
            >
              <i class="fa fa-trash"></i>
            </button>
          </div>
        </b-col>
        <b-col lg="2" md="6" sm="12">
          <div class="text-center border p-1">
            <span v-if="formatSolarPanelsTotalPower(panel)"
              >{{ formatSolarPanelsTotalPower(panel) }} kWp</span
            >
            <span v-else class="text-muted">-</span>
          </div>
        </b-col>
      </b-row>

      <!-- Total Power Row -->
      <b-row class="mt-3">
        <b-col lg="10" md="6" sm="12">
          <div class="text-right py-1">
            <strong>{{ $t('solarPanel') }} {{ $t('totalPower') }}</strong>
          </div>
        </b-col>
        <b-col lg="2" md="6" sm="12">
          <div class="text-center border p-1 bg-light">
            <strong>{{ totalPower }} kWp</strong>
          </div>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import {
  solarPanelPower,
  formatSolarPanelsTotalPower,
  formatSolarPlantTotalPanelPower,
} from '@/helpers/solarPlant';

export default {
  name: 'SolarPlantSolarPanels',
  props: {
    solarPanels: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  computed: {
    totalCount() {
      return this.solarPanels.reduce((total, panel) => total + parseInt(panel.count || 0, 10), 0);
    },
    totalPower() {
      return formatSolarPlantTotalPanelPower(this.solarPanels);
    },
  },
  methods: {
    getSolarPanelKey(panel, index) {
      return panel.item?._id || panel.item || `panel-${index}`;
    },
    getManufacturer(item) {
      return item?.solarPanel?.manufacturer || '-';
    },
    getType(item) {
      return item?.solarPanel?.type || '-';
    },
    solarPanelPower,
    formatSolarPanelsTotalPower,
    increaseCount(index) {
      const updatedSolarPanels = [...this.solarPanels];
      updatedSolarPanels[index] = {
        ...updatedSolarPanels[index],
        count: parseInt(updatedSolarPanels[index].count || 0, 10) + 1,
      };
      this.$emit('update-solar-panels', updatedSolarPanels);
    },
    decreaseCount(index) {
      const updatedSolarPanels = [...this.solarPanels];
      const currentCount = parseInt(updatedSolarPanels[index].count || 0, 10);
      if (currentCount > 1) {
        updatedSolarPanels[index] = {
          ...updatedSolarPanels[index],
          count: currentCount - 1,
        };
        this.$emit('update-solar-panels', updatedSolarPanels);
      }
    },
    updateCount(index, value) {
      const count = parseInt(value, 10);
      if (count >= 1) {
        const updatedSolarPanels = [...this.solarPanels];
        updatedSolarPanels[index] = {
          ...updatedSolarPanels[index],
          count: count,
        };
        this.$emit('update-solar-panels', updatedSolarPanels);
      }
    },
    removeSolarPanel(index) {
      const updatedSolarPanels = this.solarPanels.filter((_, i) => i !== index);
      this.$emit('update-solar-panels', updatedSolarPanels);
    },
  },
};
</script>

<style scoped lang="scss">
.quantity-input {
  width: 60px;
  text-align: center;
}

.quantity-controls {
  gap: 4px;
}

.solar-panel-row {
  padding: 0.25rem 0;

  &:hover {
    background-color: #f8f9fa;
  }
}

.solar-panel-info {
  min-height: 32px;
  display: flex;
  align-items: center;
}
</style>
